import { action, computed, observable, makeObservable } from 'mobx';
import {
  await_next_tick,
  isObject,
  isString, logger,
  PromiseState,
  promiseStates,
  watchPromise
} from '../../lib/helpers';
import {environment} from '../../lib/environment';
import {httpClient} from '../utils/httpClient';
import {localStorageUtils} from '../../lib/helpers/misc/local-storage';
import {ContentTreeDto} from '../models/content';
import {ContentLookup, VersionedContentLookup} from '../models/content-lookup';
import {prepareContentLookup} from '../utils/prepareContentLookup';

interface UseVersionResponse {
  UseVersion: string;
}

function isUseVersionResponse(value: any): value is UseVersionResponse {
  return value !== null && value !== undefined && typeof value === 'object' &&
    typeof (value as UseVersionResponse).UseVersion === 'string';
}

const localStorageKey = 'content-tree';

interface LocalStorageEntry {
  Version: string;
  Data: ContentTreeDto;
}

const b = promiseStates.b;

class ContentLookupServiceClass {

  @observable.ref
  private _state: PromiseState<VersionedContentLookup>;

  @computed
  get state(): PromiseState<VersionedContentLookup> {
    return this._state;
  }

  @observable.ref
  private _lookup: VersionedContentLookup | null;

  @computed
  get lookup(): VersionedContentLookup | null {
    return this._lookup;
  }

  @action
  private updateState(state: PromiseState<VersionedContentLookup>) {
    this._state = state;
    if (state.isResolved)
      this._lookup = state.value;
    else
      this._lookup = null;
  }

  private localStorageChecked: boolean;

  private pending: PromiseState<VersionedContentLookup> | null;

  constructor() {
    makeObservable(this);
    this._state = b.pending();
    this._lookup = null;
    this.localStorageChecked = false;
    this.pending = null;
  }

  @action
  public load = (): PromiseState<VersionedContentLookup> => {
    if (this.state.isResolved)
      return b.resolve(this.state.value);

    return this.refresh();
  };

  @action
  public refresh = (): PromiseState<VersionedContentLookup> => {
    if (this.pending !== null)
      return this.pending;
    const promise: Promise<VersionedContentLookup> = this._refresh();
    this.pending = watchPromise(promise);
    const result = this.pending;
    promise.then(() => this.pending = null)
      .catch(() => this.pending = null);
    return result;
  };

  private async _refresh(): Promise<VersionedContentLookup> {
    let url = `${environment.apiServer}/content/content-tree/probe`;
    let whichVersion: UseVersionResponse;

    if (this.state.isRejected)
      this.updateState(b.pending());

    try {
      const response = await httpClient.axios.get<UseVersionResponse>(url);
      whichVersion = response.data;
    } catch (error) {
      this.updateState(b.reject(error));
      throw error;
    }

    while (true) {
      let version = whichVersion.UseVersion;
      let lookup: ContentLookup;
      let stored: LocalStorageEntry | null;

      if (!isString(version)) {
        this.updateState(b.reject('Unexpected response: Bad value for version'));
        throw new Error('Unexpected response: Bad value for version');
      }

      let current = this.lookup;
      if (current !== null && current.version === version) {
        return current;
      }

      if (!this.localStorageChecked) {
        this.localStorageChecked = true;
        stored = localStorageUtils.load(localStorageKey);
        if (isObject(stored) && version === stored.Version) {
          await await_next_tick();
          lookup = prepareContentLookup(stored.Data);
          await await_next_tick();
          const result: VersionedContentLookup = {version: version, lookup: lookup};
          this.updateState(b.resolve(result));
          return result;
        }
      }

      localStorageUtils.remove(localStorageKey);

      if (!this.state.isPending)
        this.updateState(b.pending());

      url = `${environment.apiServer}/content/content-tree/${version}`;
      let value: ContentTreeDto | UseVersionResponse;
      try {
        const response = await httpClient.axios.get<ContentTreeDto | UseVersionResponse>(url);
        value = response.data;
      } catch (error) {
        this.updateState(b.reject(error));
        throw error;
      }

      if (isUseVersionResponse(value)) {
        whichVersion = value;
        continue;
      }

      let data = value as ContentTreeDto;
      if (!Array.isArray(data.Languages)) {
        this.updateState(b.reject('Unexpected response. No languages array.'));
        throw new Error('Unexpected response. No languages array.');
      }

      stored = {Version: version, Data: data};
      await await_next_tick();
      localStorageUtils.save(localStorageKey, stored);
      await await_next_tick();
      lookup = prepareContentLookup(data);
      await await_next_tick();
      const result = {version: version, lookup: lookup};
      this.updateState(b.resolve(result));
      return result;
    }
  }
}

export const contentLookupService = new ContentLookupServiceClass();
